import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { isNil } from 'lodash';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  CachingUtilityService,
  Device,
  DevicesService,
  DropDownConfiguration,
  DropDownData,
  EntityCacheProvider,
  LogoWidgetService,
  TestListService,
  TestMode,
  TestServerConfigService,
  TestTypeValue,
  UserAuth,
  ValuesExchangeProvider,
  ApplicationsService,
  IApplication,
  UserService,
  LicensesPageService
} from '@nrwl/common-lib';
import {
  BaseSetupTab,
  IComponentStateHistory,
  IValueChangedEvent,
  outputParams,
  REMOVED
} from '@nrwl/test-setup';
import { filter, forEach, get } from 'lodash';
import { Subject, Subscription } from 'rxjs';
import { ServiceSetupService } from '../../service-setup.service';
import { takeUntil } from 'rxjs/operators';
import { ServiceConfigFieldMapping as ConfigFieldMapping } from '../../json/service-agent-model';
import { ScheduleLicense } from '@nrwl/licenses';

@Component({
  selector: 'nrwl-service-setup-basic-tab',
  templateUrl: './service-setup-basic-tab.component.html',
  styleUrls: ['./service-setup-basic-tab.component.scss']
})
export class ServiceSetupBasicTabComponent implements OnInit, OnDestroy {
  @ViewChild('cidrInput') el: ElementRef;
  @Input() tab: BaseSetupTab;
  @Input() componentId: string;
  @Input() stateHistory?: IComponentStateHistory;
  // eslint-disable-next-line @typescript-eslint/ban-types
  @Input() restoreValues?: Function;
  @Input() previousOutput: outputParams;
  // eslint-disable-next-line @typescript-eslint/ban-types
  @Input() restoreAgentBasedConfig?: Function;
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onDataValueChange = new EventEmitter<IValueChangedEvent>();
  @ViewChild('currentForm', { static: true }) form: NgForm;
  //eslint-disable-next-line
  // eslint-disable-next-line
  dialogRef: MatDialogRef<any>;
  showWarning: boolean;
  warningMessage: string;
  // eslint-disable-next-line
  addNewApplication = false;
  newAppAssigned = false;
  dropdownApplication: DropDownData[];
  dropdownConfiguration: DropDownConfiguration;
  newTestMode = true;
  editMode = false;
  cloneMode = false;
  subContentList = [];
  subContentList1 = [];
  appDropDown = [];
  testName: string;
  actualTestName: string;
  showReset = false;
  imagePath: string;
  repeatScheduleVal = '1min';
  endScheduleVal: string;
  startScheduleVal = 'Now';
  selectedApp: DropDownData;
  allTestNames: Array<string>;
  nameExist: boolean;
  cacheObj: Subscription;
  hasReadWriteRole = false;
  testMode = TestMode.DEFAULT;
  testType = TestTypeValue.SERVICE_CONTINUITY;
  selectedOriginator: Device;
  private readonly unsubscribe: Subject<void> = new Subject();
  serviceScheduleLicense = ScheduleLicense.SERVICE_CONTINUITY;
  isDisabledScheduler = false;
  thresholdForCritical: number;
  thresholdForWarning: number;
  warningThersholds = {
    max: 100,
    min: 1
  };
  criticalThersholds = {
    max: 99,
    min: 0
  };
  constructor(
    private readonly licenseService: LicensesPageService,
    public setupService: ServiceSetupService,
    readonly entityCache: EntityCacheProvider,
    readonly serverConfigService: TestServerConfigService,
    readonly logoWidgetService: LogoWidgetService,
    readonly cachingUtilityService: CachingUtilityService,
    readonly exchange: ValuesExchangeProvider,
    readonly applicationsService: ApplicationsService,
    private dialog: MatDialog,
    private testListService: TestListService,
    private userAuth: UserAuth,
    private readonly deviceService: DevicesService,
    private readonly userService: UserService
  ) {
    this.showWarning = false;
    this.nameExist = false;
  }

  ngOnInit(): void {
    this.initilizeData();
    this.newTestMode = this.setupService.mode === 'new';
    this.editMode = this.setupService.editMode;
    this.cloneMode = this.setupService.mode === 'clone';
    if (!(this.editMode || this.cloneMode)) {
      this.testName = `${this.testName}-${this.userService.getUserName()}-${new Date().getTime()}`;
    }
    this.cacheObj = this.testListService.cache.subscribe(data => {
      this.allTestNames = data.map(item => item.escapedName);
      if (this.testName) {
        this.checkIfNameAlreadyExist();
      }
    });
    this.licenseService.activeTestLicenseDetails.subscribe(licenseData => {
      if (licenseData?.length) {
        this.isDisabledScheduler = !licenseData.some(license =>
          this.serviceScheduleLicense?.includes(license.featureName)
        );
      }
      this.isDisabledScheduler
        ? this.onEndScheduleValChange('on-demand')
        : this.onEndScheduleValChange('on-going');
    });
  }

  initilizeData(): void {
    this.restoreAgentBasedConfig();
    this.restoreFormValues();
    this.tab.form = this.form;
    this.getDropDownConfiguration();
    this.hasReadWriteRole = this.userAuth.isAuthedFor('READ_WRITE');
    this.entityCache
      .findEntityCache('application')
      .getValuesAsync()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(appList => {
        const filteredList = filter(
          appList,
          app =>
            get(app.applicationData, 'applicationType') === 'User-Defined-Url' &&
            !app.applicationData.deleted
        );
        forEach(filteredList, element => {
          element.applicationData.hidden = false;
          this.subContentList.push(element.applicationData);
        });
        this.appDropDown = this.getAppDataInFormat(this.subContentList);
        this.populateApplication();
      });
  }

  nameUpdated(): void {
    this.nameExist = false;
    if (this.testName) {
      const pattern = new RegExp('[?%/]');
      if (pattern.test(this.testName)) {
        this.form.controls.testName?.setErrors({ regex: 'wrong' });
        this.form.controls.testName?.markAsDirty();
      }
      this.checkIfNameAlreadyExist();
    }
  }

  restoreFieldValues(field: string): any {
    const data = this.stateHistory(this.componentId, field);
    if (data && !isNil(data.value) && data.value !== REMOVED) {
      return data.value;
    }
    return null;
  }

  restoreFormValues(): void {
    this.testName = this.restoreFieldValues(ConfigFieldMapping.ALIAS);
    this.actualTestName = this.testName;
    this.endScheduleVal = this.restoreFieldValues(ConfigFieldMapping.RUN_TYPE);
    this.selectedOriginator = this.restoreFieldValues(ConfigFieldMapping.SELECTED_ORIGINATOR);
    this.thresholdForCritical = this.restoreFieldValues(ConfigFieldMapping.THRESHOLD_CRITICALL);
    this.thresholdForWarning = this.restoreFieldValues(ConfigFieldMapping.THRESHOLD_WARNING);
    this.onChangeThreshold();
  }

  onChangeThreshold(): void {
    const isVaild = this.thresholdForWarning > this.thresholdForCritical;
    if (isVaild) {
      this.criticalThersholds.max = this.thresholdForWarning - 1;
      this.warningThersholds.min = this.thresholdForCritical + 1;
    }
    this.onDataEmit('thresholdWarning', this.thresholdForWarning, isVaild);
    this.onDataEmit('thresholdCritical', this.thresholdForCritical, isVaild);
  }

  onEndScheduleValChange(endVal: string): void {
    this.endScheduleVal = endVal;
    this.onDataEmit(ConfigFieldMapping.RUN_TYPE, this.endScheduleVal, true);
  }

  // eslint-disable-next-line
  onDataEmit(key: string, newValue: any, isValid: boolean): void {
    this.onDataValueChange.emit({
      componentId: this.componentId,
      key: key,
      newValue: newValue,
      isValid: isValid
    });
  }

  populateApplication(): void {
    const appId = this.restoreFieldValues(ConfigFieldMapping.SELECTED_APP_ID);
    if (appId) {
      this.selectedApp = this.appDropDown.find(x => x.key == appId);
      this.showReset = false;
      this.newAppAssigned = true;
      this.onDataEmit(ConfigFieldMapping.SLECTED_APP, this.selectedApp, true);
    }
  }

  addNewApp(): void {
    this.addNewApplication = true;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  onAgentChange(data: any): void {
    const engineAInfo = [],
      selectedOriginator = [];
    let dataValue;
    if (!data.length) {
      dataValue = [data];
    } else {
      dataValue = data;
    }
    for (let i = 0; i < dataValue.length; i++) {
      const originatorInfo = this.deviceService.findDeviceByID(dataValue[i].originatorId);
      selectedOriginator.push(originatorInfo);
    }
    this.onDataEmit('selectedOriginator', selectedOriginator, true);
  }

  assignApp(event: IApplication): void {
    const newAppData = {
      value: '',
      key: 0,
      subContent: '',
      matOptionprefixSrcImage: '',
      curlURL: ''
    };
    newAppData.key = event.id;
    newAppData.value = event.name;
    newAppData.subContent = event.url;
    newAppData.curlURL = event.curlUrl;
    newAppData.matOptionprefixSrcImage = this.updateIcon(event.url, 'images/cloud.svg');
    this.appDropDown.push(newAppData);
    this.appDropDown = [...this.appDropDown];
    this.selectedApp = this.appDropDown.find(y => y.key.toString() === event.id.toString());
    this.showReset = true;
    this.addNewApplication = false;
    this.newAppAssigned = true;
    this.onDataEmit(ConfigFieldMapping.SLECTED_APP, this.selectedApp, true);
  }
  getDropDownConfiguration(): void {
    this.dropdownConfiguration = {
      isDisableRipple: true,
      outline: 'outline',
      isDisableOptionCentering: true,
      className: 'app-dropdown',
      panelClass: 'app-panel',
      placeholderValue: 'Select application',
      isSearchable: true,
      selectTriggerMatIcon: 'display_settings',
      isDividerEnableInOptions: true,
      isSelectAll: true,
      isWarningAvailable: true,
      isDisabled: this.editMode ? true : false,
      isLogoImage: true,
      searchConfiguration: {
        isNameAllowed: true,
        isSubContentAllowed: true,
        isSubContent1Allowed: true,
        isSubDetailsAllowed: true
      },
      enableRowIcons: false
    };
  }

  // eslint-disable-next-line
  getAppDataInFormat(listData: Array<IApplication>): DropDownData[] {
    return this.getDropDownData(listData);
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getDropDownData(appList: Array<IApplication>): DropDownData[] {
    const dropDownData = [];
    let tempData: DropDownData = {
      value: '',
      key: '',
      subContent: '',
      matOptionprefixSrcImage: '',
      curlURL: ''
    };
    appList?.map(element => {
      tempData = Object.assign({}, tempData);
      tempData.matOptionprefixSrcImage = this.updateIcon(element.url, 'images/cloud.svg');
      tempData.value = element.name;
      tempData.subContent = element.url;
      tempData.key = element.id.toString();
      tempData.curlURL = element.curlUrl;
      dropDownData.push(tempData);
    });
    return dropDownData;
  }

  updateIcon(serviceUrl: string, defaultUrl: string): string {
    const serviceUrlCopy = serviceUrl;
    if (serviceUrl && serviceUrl.indexOf('tcp:') < 0) {
      this.logoWidgetService.getImage(serviceUrl, defaultUrl, (originalUrl, imgData) => {
        if (serviceUrlCopy !== originalUrl) {
          return;
        }
        this.imagePath = imgData;
      });
    } else {
      this.imagePath = defaultUrl;
    }
    return this.imagePath;
  }

  resetApp(): void {
    this.selectedApp = { value: '', key: '', subContent: '', matOptionprefixSrcImage: '' };
    this.showReset = false;
    this.newAppAssigned = false;
    this.onDataEmit('selectedApp', this.selectedApp, false);
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  applicationSelection(event: DropDownData): void {
    this.showReset = true;
    this.addNewApplication = false;
    this.newAppAssigned = true;
    this.selectedApp = this.appDropDown.find(x => x.key === event.key);
    this.onDataEmit('selectedApp', this.selectedApp, true);
  }
  checkIfNameAlreadyExist(): void {
    if (this.editMode) {
      this.allTestNames = this.allTestNames.filter(name => name !== this.actualTestName);
    }
    if (this.allTestNames.some(item => item.toLowerCase() === this.testName.toLowerCase())) {
      this.onDataEmit('alias', null, false);
      this.onDataEmit('testName', null, false);
      this.nameExist = true;
    } else {
      this.nameExist = false;
      this.onDataEmit('alias', this.testName, true);
      this.onDataEmit('testName', this.testName, true);
    }
  }
  closeAddApplicationWindow(close: boolean): void {
    this.addNewApplication = !close;
  }
  ngOnDestroy() {
    if (this.cacheObj) {
      this.cacheObj.unsubscribe();
    }
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
